<template>
  <div class="d-flex flex-column text-center">
    <div style="zindex: 10; min-height: 350px" class="mt-2">
      <div class="mt-6">
        <v-form v-model="validGrossWeight">
          <div class="mb-5">
            <div class="text-overline font-weight-light">
              {{
                currentEntry.id
                  ? $vuetify.lang.t("$vuetify.worksheet.editEntry")
                  : $vuetify.lang.t("$vuetify.base.entry")
              }}
              {{ currentEntryIndex }}
            </div>
          </div>

          <div class="d-flex justify-center">
            <div v-if="worksheetTypeFields.hasInventorySelection">
              <v-btn
                v-if="selectedInventory"
                @click="showInventoryDetails = true"
                class="mr-5 my-2"
                :title="`Selected inventory: ${selectedInventoryLabel}`"
                icon
              >
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
              <div v-else style="min-width: 36px"></div>
            </div>

            <v-text-field
              ref="inputField"
              v-model.number="currentEntry.grossWeight"
              style="font-size: 26px; min-width: 200px; max-width: 320px"
              outlined
              :label="$vuetify.lang.t('$vuetify.base.grossWeight')"
              type="number"
              inputmode="decimal"
              :min="worksheet.deductibleWeight"
              :disabled="loading || submitted"
              :readonly="selectedInventoryUnit"
              :rules="[minWeightRule]"
              :hint="ocrLoading ? $vuetify.lang.t('$vuetify.worksheet.readingScale') : selectedInventoryHint"
              :persistent-hint="ocrLoading"
              :validate-on-blur="false"
              autofocus
            >
              <template v-if="worksheetTypeFields.hasEntryPhoto" v-slot:prepend-inner>
                <v-btn
                  icon
                  small
                  class="mt-n1"
                  @click="openScaleCamera"
                  :title="
                    canScanLotQr
                      ? $vuetify.lang.t('$vuetify.worksheet.scanScaleOrLotQr')
                      : $vuetify.lang.t('$vuetify.worksheet.scanScale')
                  "
                  :disabled="loading || submitted || selectedInventoryUnit"
                >
                  <v-progress-circular v-if="ocrLoading" indeterminate size="20" width="2" color="primary" />
                  <v-icon v-else>mdi-camera</v-icon>
                </v-btn>
              </template>
              <!--
                Unit hint ("Kg" or "Kg/Bundle") shown ONLY when the input is empty,
                so it acts like a placeholder. Once the user enters a value the hint
                disappears and the digits get the full width of the input — important
                on narrow mobile viewports where a 26px-font suffix would otherwise
                eat ~140px and visually clip the entered value.
              -->
              <template v-slot:append>
                <span
                  v-if="productInfo && (currentEntry.grossWeight === null || currentEntry.grossWeight === '' || currentEntry.grossWeight === undefined)"
                  class="mr-2 text--secondary"
                >
                  {{
                    productInfo.measureType == "Unit"
                      ? `${productInfo.measureWeight}/${productInfo.measureUnit}`
                      : productInfo.measureWeight
                  }}
                </span>
              </template>
            </v-text-field>

            <v-file-input
              ref="scaleCamera"
              v-show="false"
              capture="environment"
              accept="image/*"
              v-model="scaleCameraFile"
              @change="onScaleCameraCapture"
            />

            <!-- Off-screen host html5-qrcode needs to decode the captured
                 still photo (canvases are appended here, never displayed) -->
            <div :id="qrFileScannerId" style="display: none"></div>

            <div class="d-flex" v-if="canSelectInventory">
              <v-btn
                icon
                class="ml-5 my-2"
                v-if="
                  worksheet.productId ||
                  (worksheet.products && worksheet.products.length > 0)
                "
                @click="openInventorySelection()"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
              <!-- Live scanning, not a still photo. A scanner points the
                   camera at the code and reads frame after frame with
                   continuous autofocus; one still has to get focus, distance
                   and framing right in a single shot, and on a thermal label
                   it often does not. Photo capture was tried and could not
                   read labels that this reads fine. -->
              <div class="mt-2 ml-2">
                <qr-scanner
                  :manualProcess="true"
                  @captured="processScannedInventory"
                />
              </div>
            </div>
          </div>

          <!-- <div class="text-caption font-weight-light mb-5" v-if="selectedInventory">Selected inventory: {{selectedInventoryHint}}</div> -->
        </v-form>

        <div
          v-if="hasDeductibleWeight"
          class="text-subtitle-2 mt-4 d-flex justify-center"
        >
          <div class="d-flex flex-column flex-sm-row justify-center">
            <span class="mx-2">{{
              $vuetify.lang.t("$vuetify.base.deductibleWeight")
            }}</span>
            <span
              >{{ worksheet.deductibleWeight | formatNumber }}
              {{ productInfo ? productInfo.measureWeight : "" }}</span
            >
          </div>
          <v-divider vertical class="mx-4" />
          <div class="d-flex flex-column flex-sm-row justify-center">
            <span class="mx-2">{{
              $vuetify.lang.t("$vuetify.base.netWeight")
            }}</span>
            <span
              >{{ netWeight | formatNumber }}
              {{ productInfo ? productInfo.measureWeight : "" }}</span
            >
          </div>
        </div>
      </div>

      <div
        style="min-height: 130px"
        :class="`d-flex ${entryIconsLayout} justify-center`"
      >
        <div
          v-if="worksheetTypeFields.hasEntryNotes"
          class="d-flex justify-center mt-4 mx-2"
        >
          <v-btn
            v-show="!showEntryNotes"
            icon
            @click="showEntryNotes = !showEntryNotes"
            color="primary"
            ><v-icon x-large>mdi-note-edit</v-icon></v-btn
          >
          <v-expand-x-transition>
            <div v-show="showEntryNotes">
              <v-textarea
                append-icon="mdi-close"
                style="min-width: 300px"
                @click:append="showEntryNotes = false"
                :label="$vuetify.lang.t('$vuetify.base.notes')"
                v-model="currentEntry.notes"
                :disabled="loading || submitted"
                hide-details
                :rows="3"
                outlined
              />
            </div>
          </v-expand-x-transition>
          <div
            v-if="currentEntry.notes && !showEntryNotes"
            class="font-weight-light text-subtitle-2 mx-2"
            style="white-space: pre"
          >
            {{ currentEntry.notes }}
          </div>
        </div>

        <div class="d-flex justify-center">
          <image-upload
            ref="imageUpload"
            class="mt-4 mx-2"
            v-if="worksheetTypeFields.hasEntryPhoto"
            v-model="currentEntry.files"
            :key="`${currentEntry.id}-${addTime}`"
            :loading="loading"
            :disabled="loading || submitted"
          />
        </div>

      </div>

      <div class="mt-5">
        <v-btn
          :loading="loading"
          :disabled="submitted || !validGrossWeight || ocrLoading"
          @click="saveWorksheetEntry(worksheet, currentEntry)"
          color="primary"
        >
          {{
            currentEntry.id
              ? $vuetify.lang.t("$vuetify.base.update")
              : $vuetify.lang.t("$vuetify.base.add")
          }}
        </v-btn>
      </div>
    </div>

    <v-dialog
      style="z-index: 1000"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="inventoryLookupDialog"
    >
      <v-card>
        <v-card-title class="d-flex justify-end">
          <v-btn icon @click="inventoryLookupDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="px-4 px-sm-6 px-md-8">
          <inventory-selection-by-product
            @change="inventoryChanged"
            :key="timestamp"
            :productId="worksheet.productId"
            :products="worksheet.products"
            :worksheetId="worksheet.id"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="weightDeviationDialog" persistent max-width="500" style="z-index: 1001">
      <v-card>
        <v-card-title class="warning--text">
          <v-icon left color="warning">mdi-alert</v-icon>
          {{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.title") }}
        </v-card-title>
        <v-card-text v-if="weightDeviationDetails">
          <p class="text-body-1 mb-3">
            {{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.message") }}
          </p>
          <v-simple-table dense>
            <tbody>
              <tr>
                <td class="font-weight-medium">{{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.entered") }}</td>
                <td>{{ currentEntry && currentEntry.grossWeight }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.historicalAvg") }}</td>
                <td>{{ weightDeviationDetails.mean }} ± {{ weightDeviationDetails.stddev }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.deviation") }}</td>
                <td>{{ weightDeviationDetails.deviationPct }}% ({{ weightDeviationDetails.deviationStddev }}σ)</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.sampleSize") }}</td>
                <td>{{ weightDeviationDetails.sampleSize }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="text-caption mt-3 mb-0">
            {{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.action") }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelWeightDeviation">
            {{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.reEnter") }}
          </v-btn>
          <v-btn color="warning" :loading="loading" @click="confirmWeightDeviation">
            {{ $vuetify.lang.t("$vuetify.worksheet.weightDeviation.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-bottom-sheet scrollable v-model="showInventoryDetails">
      <v-card>
        <v-card-title class="justify-center">
          <v-btn class="mt-6" text @click="showInventoryDetails = false">
            {{ this.$vuetify.lang.t("$vuetify.close") }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="selectedInventory">
            <v-divider />
            <div v-if="selectedInventoryUnit">
              <div class="d-flex justify-space-around text-subtitle-2">
                <span
                  >{{
                    this.$vuetify.lang.t("$vuetify.worksheet.selectedUnit")
                  }}:</span
                >
                <div class="d-flex">
                  <span>{{ selectedInventoryUnit.code }}</span>
                  <v-divider class="mx-2" vertical />
                  <span
                    >{{ selectedInventoryUnit.amountPerUnit | formatNumber }}
                    {{ selectedInventoryUnit.unitMeasure }}</span
                  >
                  <v-divider class="mx-2" vertical />
                  <span
                    >{{ selectedInventoryUnit.unitShare }}
                    {{ productInfo ? productInfo.measureUnit : "" }}</span
                  >
                </div>
              </div>
            </div>

            <inventory-details
              :key="selectedInventory ? selectedInventory.id : 0"
              v-model="selectedInventory"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>

    <!-- Only when a lot code genuinely matches more than one in-stock lot.
         Picking one automatically could fill the entry from the wrong pile. -->
    <v-dialog v-model="showLmeChoice" max-width="460">
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ $vuetify.lang.t('$vuetify.worksheet.chooseScannedLot') }}
        </v-card-title>
        <v-list dense>
          <v-list-item
            v-for="(m, i) in lmeMatches"
            :key="i"
            @click="chooseLmeMatch(m)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ m.unitDetail.code }}</v-list-item-title>
              <v-list-item-subtitle>
                #{{ m.inventory.id }} · {{ m.inventory.documentNumber }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <!-- Scale OCR scanning overlay -->
    <v-overlay :value="ocrLoading" opacity="0.85" z-index="999">
      <div class="d-flex flex-column align-center" style="max-width: 320px;">
        <div style="position: relative; width: 280px; height: 210px; border-radius: 8px; overflow: hidden;">
          <v-img
            v-if="ocrPreviewUrl"
            width="280"
            height="210"
            cover
            :src="ocrPreviewUrl"
          />
          <div class="scan-line" />
          <div style="position: absolute; inset: 0; border: 2px solid rgba(33,150,243,0.6); border-radius: 8px; pointer-events: none;" />
        </div>
        <div class="mt-4 text-center white--text">
          <v-progress-circular indeterminate size="24" width="2" color="white" class="mr-2" />
          <span class="text-subtitle-1">{{ $vuetify.lang.t("$vuetify.worksheet.readingScale") }}</span>
        </div>
      </div>
    </v-overlay>

  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const InventorySelectionByProduct = () =>
  import("@/components/inventory/InventorySelectionByProduct.vue");
const InventoryDetails = () =>
  import("@/components/inventory/InventoryDetails.vue");
const ImageUpload = () => import("@/components/common/ImageUpload.vue");
import { findInventory } from "@/services/processWorksheet.js";
import { tagReservedInventories } from "@/services/inventoryReservation.js";
import { parseLmeQrValue, findAvailableLotsByCode } from "@/utils/lmeLotQr.js";
const QrScanner = () => import("@/components/common/QrScanner.vue");
import { sumWorksheetWeight } from "@/services/worksheetServices";
import { readScaleWeight } from "@/services/scaleOcrService.js";
import { decrypt } from "@/services/CryptoService.js";

export default {
  props: {
    worksheet: {
      type: Object,
      required: true,
    },
    currentEntry: {
      type: Object,
      required: true,
    },
    worksheetTypeFields: {
      type: Object,
      required: true,
    },
    productInfo: {
      type: Object,
      required: false,
    },
  },
  data: () => {
    return {
      loading: false,
      inventoryLookupDialog: false,
      validGrossWeight: false,
      showEntryNotes: false,
      addTime: Date.now(),
      imageData: null,
      sum: { grossWeight: null, netWeight: 0 },
      selectedInventory: null,
      selectedInventoryUnit: null,
      timestamp: Date.now(),
      showInventoryDetails: false,
      ocrLoading: false,
      ocrPreviewUrl: null,
      scaleCameraFile: null,
      lmeMatches: [],
      showLmeChoice: false,
      scaleImageBlob: null,
      weightDeviationDialog: false,
      weightDeviationDetails: null,
      weightDeviationContext: null, // { mode: 'create'|'update', entry, data, isGrouped, groupId }
    };
  },
  watch: {
    currentEntry: function (newVal, oldVal) {
      if (newVal && oldVal && newVal.inventoryId != oldVal.inventoryId) {
        this.selectedInventory = null;
        this.selectedInventoryUnit = null;
        console.log("selected inventory changed...");
      }
    },
  },
  computed: {
    ...mapGetters("auth", ["currentUser"]),
    weightUnit() {
      if (this.productInfo) {
        return this.productInfo.measureType == "Unit"
          ? this.productInfo.measureUnit
          : this.productInfo.measureWeight;
      } else {
        return null;
      }
    },
    entryIconsLayout() {
      const hasFiles =
        this.currentEntry.files && this.currentEntry.files.length > 0;
      const hasNotes = this.showEntryNotes || this.currentEntry.notes;
      const useColumn = hasFiles || hasNotes;
      return useColumn ? "flex-column" : "flex-row";
    },
    hasDeductibleWeight() {
      return (
        this.worksheetTypeFields.hasDeductibleWeight &&
        this.worksheet.deductibleWeight &&
        this.worksheet.deductibleWeight > 0
      );
    },
    netWeight() {
      return (
        (this.currentEntry.grossWeight ? this.currentEntry.grossWeight : 0) -
        this.worksheet.deductibleWeight
      );
    },
    // Visibility of the magnifier + QR-scanner buttons: only where entries are
    // restricted to inventory, which is what inventorySelectionOnly means.
    canSelectInventory() {
      return !!(
        this.worksheetTypeFields.hasInventorySelection &&
        this.worksheet.inventorySelectionOnly
      );
    },
    // Reading a lot QR out of the entry photo is useful wherever the worksheet
    // type supports inventory selection at all — NOT only where it's forced.
    // Gating this on inventorySelectionOnly (as the buttons above are) silently
    // disabled it on ~65% of eligible worksheets, e.g. SB26-037.
    canScanLotQr() {
      return !!this.worksheetTypeFields.hasInventorySelection;
    },
    qrFileScannerId() {
      return `entryQrFileScanner-${this._uid}`;
    },
    hasSupplierOrCustomerOrSettings() {
      return (
        this.worksheetTypeFields &&
        (this.worksheetTypeFields.hasWeightSettings ||
          this.worksheetTypeFields.hasSupplier ||
          this.worksheetTypeFields.hasCustomer)
      );
    },
    hasEntries() {
      return this.worksheet.entries && this.worksheet.entries.length > 0;
    },
    currentEntryIndex() {
      if (this.worksheet.entries && this.worksheet.entries.length > 0) {
        if (this.currentEntry.id) {
          const index = this.worksheet.entries.findIndex(
            (e) => e.id == this.currentEntry.id
          );
          return index + 1;
        } else {
          return this.worksheet.entries.length + 1;
        }
      } else {
        return 1;
      }
    },
    submitted() {
      if (
        this.worksheet &&
        ["SUBMITTED", "USED"].includes(this.worksheet.status)
      ) {
        return true;
      } else {
        return false;
      }
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    selectedInventoryHint() {
      if (!this.worksheetTypeFields.hasInventorySelection) {
        return null;
      }

      if (this.selectedInventory) {
        const inventoryDate = this.$options.filters.formatDateYear(
          this.selectedInventory.stockTime
        );
        if (this.selectedInventoryUnit) {
          const productMeasureUnit = this.productInfo
            ? this.productInfo.measureUnit
            : "";
          const productMeasureWeight = this.productInfo
            ? this.productInfo.measureWeight
            : "";
          return `${inventoryDate} - ${
            this.currentEntry.unit
          } ${productMeasureUnit} @ ${this.$options.filters.formatNumber(
            this.currentEntry.amountPerUnit
          )}${productMeasureWeight}/${productMeasureUnit}`;
        } else {
          return `${inventoryDate} - ${this.$options.filters.formatNumber(
            this.selectedInventory.quantityAvailable -
              this.selectedInventory.reservedQuantity
          )} available`;
        }
      } else {
        return this.$vuetify.lang.t("$vuetify.worksheet.noInventorySelected");
      }
    },
  },
  components: {
    QrScanner,
    ImageUpload,
    InventorySelectionByProduct,
    InventoryDetails,
  },
  async created() {
    this.sumWeight();

    // Ensure 'entries' is initialized as an array
    if (!Array.isArray(this.worksheet.entries)) {
      this.$set(this.worksheet, 'entries', []);
    }
    
    if (this.currentEntry && this.currentEntry.inventoryId) {
      const inventory = await this.fetchInventory(
        this.currentEntry.inventoryId
      );
      this.selectedInventory = inventory;
      this.selectedInventoryUnit = inventory.unitDetails.find(
        (d) => d.id == this.currentEntry.inventoryUnitId
      );
    }
  },
  methods: {
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    openScaleCamera() {
      const fileElement =
        this.$refs.scaleCamera.$el.querySelector('input[type="file"]');
      if (fileElement) {
        fileElement.click();
      }
    },
    async onScaleCameraCapture() {
      if (!this.scaleCameraFile) return;

      const file = this.scaleCameraFile;

      try {
        // Show preview immediately
        this.ocrPreviewUrl = URL.createObjectURL(file);
        this.ocrLoading = true;

        // The same shot may be of a lot label rather than the scale display —
        // look for a QR code first so one button covers both: photograph the
        // scale to read the weight, or photograph the label to pick the lot.
        if (this.canScanLotQr) {
          const qrCodeText = await this.decodeQrCodeFromImage(file);
          if (qrCodeText) {
            await this.processScannedInventory(this.parseQrCodePayload(qrCodeText));
            return;
          }
        }

        // Compress image (same as ImageUpload)
        const { fromBlob } = await import("image-resize-compress");
        let blob;
        if (file.size < 300000) {
          blob = file;
        } else {
          blob = await fromBlob(file, 100, 860, "auto", null);
        }
        const fileType = blob.type.split("/");
        const extension = fileType && fileType.length > 1 ? fileType[1] : "jpg";
        blob.name = blob.name || `scale.${extension}`;

        // Send to OCR
        const response = await readScaleWeight(blob);
        const result = response.data;

        if (result.success && result.weight !== null) {
          this.currentEntry.grossWeight = result.weight;
          this.currentEntry.weightEntryMethod = "SCAN";
          this.addSuccessMessage(
            this.$vuetify.lang.t(
              "$vuetify.worksheet.ocrWeightApplied",
              result.weight
            )
          );
        } else {
          this.addErrorMessage(
            this.$vuetify.lang.t("$vuetify.worksheet.ocrWeightNotRead")
          );
        }

        // Attach scale image to the entry
        if (this.$refs.imageUpload) {
          // Remove previous scale image if it still exists
          if (this.scaleImageBlob) {
            const prevIdx = this.$refs.imageUpload.compressedImages.indexOf(this.scaleImageBlob);
            if (prevIdx >= 0) {
              this.$refs.imageUpload.remove(prevIdx);
            }
          }
          this.$refs.imageUpload.addFile(blob);
          this.scaleImageBlob = blob;
        }
      } catch (error) {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.ocrFailed")
        );
      } finally {
        this.ocrLoading = false;
        this.ocrPreviewUrl = null;
        this.scaleCameraFile = null;
      }
    },
    // Returns the decoded QR text, or null when the photo holds no QR code
    // (the normal case for a scale shot — the caller then falls back to OCR).
    async decodeQrCodeFromImage(file) {
      let scanner = null;
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import(
          "html5-qrcode"
        );
        scanner = new Html5Qrcode(this.qrFileScannerId, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
        return await scanner.scanFile(file, /* showImage= */ false);
      } catch (error) {
        return null;
      } finally {
        if (scanner) {
          try {
            scanner.clear();
          } catch (error) {
            // canvases are inside a hidden div — nothing to recover from
          }
        }
      }
    },
    parseQrCodePayload(qrCodeText) {
      try {
        // Entry/lot QR codes carry an encrypted JSON payload; anything else
        // (e.g. a plain URL) comes back as-is and is rejected downstream.
        return decrypt(qrCodeText);
      } catch (error) {
        return qrCodeText;
      }
    },
    extractErrorMessage(error) {
      let data = null;
      
      // Handle both axios response structure and direct error data
      if (error.response && error.response.data) {
        data = error.response.data;
      } else if (error.data) {
        data = error.data;
      }
      
      if (data) {
        // Use the message field if available (most detailed)
        let msg = data.message || data.error || error.message || "An unexpected error occurred";
        
        // Check for specific inventory error and translate if needed
        if (msg === 'Insufficent inventory available') {
          msg = this.$vuetify.lang.t("$vuetify.worksheet.error.insufficientInventory");
        }
        
        return msg;
      }
      
      // Fall back to error message or generic message
      return error.message || "An unexpected error occurred";
    },
    async saveWorksheetEntry(worksheet, entry) {
      this.loading = true;

      if (!this.validGrossWeight) {
        this.loading = false;
        return;
      }

      if (worksheet.requirePhoto && (!entry.files || entry.files.length < 1)) {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.photoRequired")
        );
        this.loading = false;
        return;
      }

      if (worksheet.inventorySelectionOnly && entry.inventoryId == null) {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryRequired")
        );
        this.loading = false;
        return;
      }

      if (!entry.weightEntryMethod) {
        entry.weightEntryMethod = "MANUAL";
      }

      const unit = entry.unit;
      const amountPerUnit = entry.amountPerUnit;
      const grossWeight = entry.grossWeight ? entry.grossWeight : 0;
      
      // Calculate netWeight locally for client-side validation only
      // Backend is the source of truth and will recalculate netWeight
      const estimatedNetWeight =
        grossWeight -
        (worksheet.deductibleWeight ? worksheet.deductibleWeight : 0);

      //selected inventory and not inventory unit and grossWeight is greater than available
      if (
        this.selectedInventory &&
        this.selectedInventory.quantityAvailable &&
        !this.selectedInventoryUnit
      ) {
        // When editing, we need to add back the currently reserved amount
        let currentlyReserved = 0;
        if (entry.id && entry.inventoryId === this.selectedInventory.id) {
          currentlyReserved = entry.netWeight || 0;
        }
        
        const availableQuantity =
          this.selectedInventory.quantityAvailable -
          this.selectedInventory.reservedQuantity +
          currentlyReserved;
        
        if (estimatedNetWeight > availableQuantity) {
          this.loading = false;
          this.addErrorMessage(
            this.$vuetify.lang.t("$vuetify.worksheet.error.greaterThanAvailable")
          );
          return;
        }
      }

      // Note: netWeight is calculated by the backend for data integrity
      const data = {
        processWorksheetId: worksheet.id,
        unit,
        amountPerUnit,
        grossWeight,
        weightEntryMethod: entry.weightEntryMethod || "MANUAL",
        staffId: this.currentUser.uId,
        inventoryId: entry.inventoryId,
        inventoryUnitId: entry.inventoryUnitId,
      };
      if (this.worksheetTypeFields.hasEntryNotes) {
        data.notes = entry.notes;
      }
      if (entry.id) {
        if (entry.files && entry.files.length > 0) {
          const newFiles = entry.files.filter((f) => !f.id);
          if (newFiles && newFiles.length > 0) {
            await this.uploadFiles(entry.id, newFiles);
          }
        }

        const isGroupedEntry = entry._isGrouped || (entry.entryGroupId && entry.entryGroupId !== entry.id);
        const groupId = isGroupedEntry
          ? (entry.entryGroupId || entry._groupEntries[0].entryGroupId)
          : null;
        return await this.submitUpdate(entry, data, isGroupedEntry, groupId, false);
      } else {
        return await this.submitCreate(entry, data, false);
      }
    },
    async submitUpdate(entry, data, isGroupedEntry, groupId, bypassWeightCheck) {
      const config = bypassWeightCheck ? { params: { bypassWeightCheck: true } } : {};
      try {
        let resp;
        if (isGroupedEntry) {
          resp = await this.$axios.put(`/processWorksheetInput/group/${groupId}`, data, config);
          const updatedEntries = resp.data;
          if (Array.isArray(updatedEntries) && updatedEntries.length > 0) {
            updatedEntries.forEach(updated => { updated.updateTime = Date.now(); });
            this.$emit("update-entries", updatedEntries);
            this.addSuccessMessage(`Updated ${updatedEntries.length} grouped entries`);
          }
        } else {
          resp = await this.$axios.put(`/processWorksheetInput/${entry.id}`, data, config);
          const updatedEntry = resp.data;
          updatedEntry.createTime = Date.now();
          updatedEntry.updateTime = Date.now();
          this.$emit("update-entry", updatedEntry);
          this.addSuccessMessage(this.$vuetify.lang.t("$vuetify.worksheet.entryUpdated"));
        }
        this.resetEntry();
      } catch (error) {
        const errData = error.data || (error.response && error.response.data);
        const errCode = errData && errData.code;
        if (errCode === 'WEIGHT_DEVIATION' && !bypassWeightCheck) {
          this.promptWeightDeviation(errData.details, {
            mode: 'update', entry, data, isGroupedEntry, groupId,
          });
          return null;
        }
        const errorMsg = this.extractErrorMessage(error);
        this.addErrorMessage(errorMsg);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async submitCreate(entry, data, bypassWeightCheck) {
      const config = bypassWeightCheck ? { params: { bypassWeightCheck: true } } : {};
      try {
        const resp = await this.$axios.post("/processWorksheetInput", data, config);
        const savedEntries = resp.data;
        if (!Array.isArray(savedEntries) || savedEntries.length === 0) {
          throw new Error("No entries returned from server");
        }
        return await this.finalizeSavedEntries(savedEntries, entry.files);
      } catch (error) {
        const errData = error.data || (error.response && error.response.data);
        const errCode = errData && errData.code;
        if (errCode === 'INSUFFICIENT_INVENTORY') {
          return await this.createInventoryReviewEntry(data, entry.files, bypassWeightCheck);
        }
        if (errCode === 'WEIGHT_DEVIATION' && !bypassWeightCheck) {
          this.promptWeightDeviation(errData.details, {
            mode: 'create', entry, data,
          });
          return null;
        }
        const errorMsg = this.extractErrorMessage(error);
        this.addErrorMessage(errorMsg);
        return null;
      } finally {
        this.loading = false;
      }
    },
    promptWeightDeviation(details, context) {
      this.weightDeviationDetails = details;
      this.weightDeviationContext = context;
      this.weightDeviationDialog = true;
    },
    cancelWeightDeviation() {
      this.weightDeviationDialog = false;
      this.weightDeviationDetails = null;
      this.weightDeviationContext = null;
      this.focusAndSelectInput();
    },
    async confirmWeightDeviation() {
      const ctx = this.weightDeviationContext;
      this.weightDeviationDialog = false;
      this.weightDeviationDetails = null;
      this.weightDeviationContext = null;
      if (!ctx) return;
      this.loading = true;
      if (ctx.mode === 'create') {
        return await this.submitCreate(ctx.entry, ctx.data, true);
      }
      return await this.submitUpdate(ctx.entry, ctx.data, ctx.isGroupedEntry, ctx.groupId, true);
    },
    async finalizeSavedEntries(savedEntries, files) {
      if (!Array.isArray(savedEntries) || savedEntries.length === 0) {
        throw new Error("No entries returned from server");
      }

      savedEntries.forEach(saved => {
        saved.createTime = Date.now();
        saved.updateTime = Date.now();
      });

      this.$emit("add-entry", savedEntries);

      if (files && files.length > 0) {
        await this.uploadFiles(savedEntries[0].id, files);
      }

      this.resetEntry();

      const hasNeedsReview = savedEntries.some(
        savedEntry => savedEntry.inventoryDecisionStatus === "NEEDS_REVIEW"
      );

      if (hasNeedsReview) {
        this.addSuccessMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryReview.submitted")
        );
      } else if (savedEntries.length > 1) {
        this.addSuccessMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.entriesAdded", savedEntries.length)
        );
      } else {
        this.addSuccessMessage(this.$vuetify.lang.t("$vuetify.worksheet.entryAdded"));
      }

      return savedEntries;
    },
    async createInventoryReviewEntry(payload, files, bypassWeightCheck) {
      const reviewPayload = {
        ...payload,
        inventoryDecisionStatus: "NEEDS_REVIEW",
        inventoryDecisionNote: this.buildInventoryReviewNote(payload),
      };
      const config = bypassWeightCheck ? { params: { bypassWeightCheck: true } } : {};

      try {
        const resp = await this.$axios.post("/processWorksheetInput", reviewPayload, config);
        return await this.finalizeSavedEntries(resp.data, files);
      } catch (error) {
        const errorMsg = this.extractErrorMessage(error);
        this.addErrorMessage(errorMsg);
        return null;
      }
    },
    resetEntry() {
      this.sumWeight();
      this.currentEntry = { grossWeight: null };
      this.selectedInventory = null;
      this.selectedInventoryUnit = null;
      this.ocrLoading = false;
      this.ocrPreviewUrl = null;
      this.scaleCameraFile = null;
      this.scaleImageBlob = null;
      this.$emit("update:currentEntry", this.currentEntry);
      this.focusAndSelectInput();
    },
    async uploadFiles(entryId, files) {
      if (files && files.length > 0) {
        const formData = new FormData();
        for (const f of files) {
          formData.append("files", f, f.name);
        }

        this.addMessage(this.$vuetify.lang.t("$vuetify.worksheet.uploading"));
        try {
          const resp = await this.$axios.post(`/processWorksheetInput/upload/${entryId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const savedFiles = resp.data;
          const entry = this.worksheet.entries.find((e) => e.id == entryId);
          if (!entry.files) {
            entry.files = [];
          }
          savedFiles.forEach((f) => {
            entry.files.push({
              id: f.id,
              name: f.originalFileName,
              size: f.size,
            });
          });

          this.addTime = Date.now();
          this.addSuccessMessage(
            this.$vuetify.lang.t(
              "$vuetify.worksheet.uploadedXFiles",
              files.length
            )
          );
        } catch (error) {
          this.addErrorMessage(this.$vuetify.lang.t("$vuetify.worksheet.error.uploadFiles"));
        }
      }
    },

    buildInventoryReviewNote(payload) {
      const requested = this.getRequestedReviewQuantity(payload);
      if (requested <= 0) {
        return null;
      }

      const parts = ["SHORTFALL"];
      if (this.worksheet.locationId != null) {
        parts.push(`locationId:${this.worksheet.locationId}`);
      }
      if (this.worksheet.locationName) {
        parts.push(`locationName:${this.worksheet.locationName}`);
      }
      parts.push(`available:${this.formatInventoryReviewQuantity(0)}`);
      parts.push(`requested:${this.formatInventoryReviewQuantity(requested)}`);
      parts.push(`shortfall:${this.formatInventoryReviewQuantity(requested)}`);

      return `[${parts.join("|")}]`;
    },
    getRequestedReviewQuantity(payload) {
      if (!payload) {
        return 0;
      }

      const netWeight = Number(payload.netWeight);
      if (Number.isFinite(netWeight)) {
        return Math.max(netWeight, 0);
      }

      const grossWeight = Number(payload.grossWeight || 0);
      const deductibleWeight = Number(this.worksheet.deductibleWeight || 0);
      return Math.max(grossWeight - deductibleWeight, 0);
    },
    formatInventoryReviewQuantity(value) {
      const numericValue = Number(value);
      const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
      return safeValue.toFixed(2);
    },

    async sumWeight() {
      this.sum = await sumWorksheetWeight(this.worksheet);
      this.$emit("update:sum", this.sum);
    },
    focusAndSelectInput() {
      let grossWeightInput = this.$refs.inputField.$el.querySelector("input");
      setTimeout(() => {
        grossWeightInput.select();
      }, 200);
    },
    minWeightRule(v) {
      if (this.ocrLoading) {
        return true;
      }
      if (v !== 0 && !v) {
        return this.$vuetify.lang.t("$vuetify.validation.fieldRequired");
      } else if (v < 0) {
        return this.$vuetify.lang.t("$vuetify.validation.greaterThan", 0);
      } else if (
        this.worksheet.deductibleWeight &&
        v <= this.worksheet.deductibleWeight
      ) {
        return this.$vuetify.lang.t(
          "$vuetify.validation.smallerThanDeductible"
        );
      } else {
        return true;
      }
    },
    openInventorySelection() {
      this.inventoryLookupDialog = true;
      this.timestamp = Date.now();
    },
    inventoryChanged(inventory, inventoryUnit) {
      this.selectedInventory = inventory;
      this.currentEntry.inventoryId = inventory.id;
      this.selectedInventoryUnit = inventoryUnit;
      if (inventoryUnit) {
        const availableUnits =
          inventoryUnit.unitShare -
          (inventoryUnit.usedUnitShare ? inventoryUnit.usedUnitShare : 0);
        this.currentEntry.unit = availableUnits;
        this.currentEntry.amountPerUnit = inventoryUnit.amountPerUnit;
        this.currentEntry.grossWeight =
          inventoryUnit.amountPerUnit * availableUnits;
        this.currentEntry.inventoryUnitId = inventoryUnit.id;
      } else {
        // Defensive `|| 0` — if any inventory source (e.g. a future picker path)
        // forgets to tag reservedQuantity, this stays a real number instead of
        // becoming NaN and silently blanking out the weight input.
        this.currentEntry.grossWeight =
          (inventory.quantityAvailable || 0) - (inventory.reservedQuantity || 0);
      }
      this.inventoryLookupDialog = false;
    },
    // Select a scanned lot into this entry: same product check, reservation
    // tagging and weight fill whichever way the lot was identified.
    async applyScannedLot(inventory, unitDetail) {
      if (!this.allowedProduct(inventory.productId)) {
        if (this.worksheet.productName) {
          this.addErrorMessage(
            this.$vuetify.lang.t("$vuetify.worksheet.error.scanProductQR", this.worksheet.productName)
          );
        } else if (this.worksheet.products) {
          const productNames = this.worksheet.products
            .map((p) => p.name)
            .join(", ");
          this.addErrorMessage(
            this.$vuetify.lang.t("$vuetify.worksheet.error.scanProductQR", productNames)
          );
        } else {
          this.addErrorMessage(this.$vuetify.lang.t("$vuetify.worksheet.error.invalidQRCode"));
        }
        return;
      }
      await tagReservedInventories(inventory);
      this.inventoryChanged(inventory, unitDetail);
      this.showInventoryDetails = true;
    },
    // LME stickers stay as plain `BRAND|LOT-CODE` for outside scanners, so
    // convert that back to our own lot. Restricted to available stock, where
    // the code is effectively unique (43 ties in 5,795); a real tie asks
    // rather than guessing.
    async resolveLmeLot(lme) {
      try {
        const matches = await findAvailableLotsByCode(lme);
        if (matches.length === 1) {
          await this.applyScannedLot(matches[0].inventory, matches[0].unitDetail);
          return;
        }
        if (matches.length > 1) {
          this.lmeMatches = matches;
          this.showLmeChoice = true;
          return;
        }
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.error.lotNotInStock", lme.lotCode)
        );
      } catch (error) {
        this.addErrorMessage(this.extractErrorMessage(error));
      }
    },
    async chooseLmeMatch(match) {
      this.showLmeChoice = false;
      await this.applyScannedLot(match.inventory, match.unitDetail);
    },
    async processScannedInventory(qrCodeMessage) {
      const lme = parseLmeQrValue(qrCodeMessage);
      if (lme) {
        await this.resolveLmeLot(lme);
        return;
      }

      if (
        typeof qrCodeMessage == "object" &&
        (qrCodeMessage.businessType == "WorksheetEntry" ||
          qrCodeMessage.businessType == "Inventory")
      ) {
        const worksheetId = qrCodeMessage.worksheetId;
        // Entry stickers printed from WorksheetPrint encode the entry as
        // `entryId`; keep accepting `worksheetEntryId` for other QR sources.
        const worksheetEntryId =
          qrCodeMessage.worksheetEntryId || qrCodeMessage.entryId;
        try {
          const resp = await findInventory(worksheetId, worksheetEntryId);
          if (resp && resp.data) {
            await this.applyScannedLot(resp.data, resp.data.selectedUnitDetail);
          } else {
            this.addErrorMessage(this.$vuetify.lang.t("$vuetify.worksheet.error.noInventoryInfo"));
          }
        } catch (error) {
          this.addErrorMessage(this.extractErrorMessage(error));
        }
      } else {
        this.addErrorMessage(this.$vuetify.lang.t("$vuetify.worksheet.error.unrecognizableQRCode"));
      }
    },
    allowedProduct(productId) {
      let allowed = false;
      if (productId == this.worksheet.productId) {
        allowed = true;
      } else if (
        this.worksheet.products &&
        this.worksheet.products.length > 0
      ) {
        allowed = this.worksheet.products.map((p) => p.id).includes(productId);
      }

      return allowed;
    },
    async fetchInventory(inventoryId) {
      try {
        const response = await this.$axios.get("/inventory/" + inventoryId);
        this.inventory = response.data;
        return this.inventory;
      } catch (error) {
        this.inventory = null;
        this.addErrorMessage(this.$vuetify.lang.t("$vuetify.worksheet.error.inventoryNotFound", inventoryId));
        return null;
      }
    },
  },
};
</script>


<style scoped>
.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(33,150,243,0.8), transparent);
  box-shadow: 0 0 8px rgba(33,150,243,0.6);
  animation: scan 1.5s ease-in-out infinite;
  z-index: 1;
}

@keyframes scan {
  0% { top: 10%; }
  50% { top: 85%; }
  100% { top: 10%; }
}
</style>
